// src/components/CryptoChart.js
import React, { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';

const CryptoChart = ({ coin, symbol, data, timeRange, currentPrice, priceChange }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  
  // Preisformatierung abhängig vom Basispreis
  const getPriceFormat = (price) => {
    if (price < 1) {
      return {
        type: 'price',
        precision: 4,
        minMove: 0.0001,
      };
    } else if (price < 100) {
      return {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      };
    } else {
      return {
        type: 'price',
        precision: 0,
        minMove: 1,
      };
    }
  };

  // Chart initialisieren
  useEffect(() => {
    if (chartContainerRef.current && data && data.length > 0 && !chartRef.current) {
      // Chart erstellen
      chartRef.current = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 300,
        layout: {
          backgroundColor: '#1e293b',  // Dunkler Hintergrund
          textColor: '#f3f4f6',        // Helle Schrift
        },
        grid: {
          vertLines: {
            color: '#374151',          // Dunklere Gitterlinien
          },
          horzLines: {
            color: '#374151',
          },
        },
        timeScale: {
          borderColor: '#4b5563',      // Dunklere Rahmen
          timeVisible: true,
          secondsVisible: false,
        },
        rightPriceScale: {
          borderColor: '#4b5563',
        },
      });

      // Candlestick-Serie hinzufügen
      candleSeriesRef.current = chartRef.current.addCandlestickSeries({
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
        priceFormat: getPriceFormat(currentPrice),
      });

      // Volume-Serie hinzufügen
      volumeSeriesRef.current = chartRef.current.addHistogramSeries({
        color: '#3b82f6',
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
      });

      // Daten formatieren und setzen
      const formattedCandles = data.map(item => ({
        time: item.timestamp,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
      }));

      const formattedVolumes = data.map(item => ({
        time: item.timestamp,
        value: item.volume,
        color: item.close >= item.open ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)',
      }));

      candleSeriesRef.current.setData(formattedCandles);
      volumeSeriesRef.current.setData(formattedVolumes);

      // Window resize handler
      const handleResize = () => {
        if (chartRef.current && chartContainerRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
        }
      };
    }
  }, [data, currentPrice]);

  // Update des Charts bei Änderung der Daten
  useEffect(() => {
    if (chartRef.current && candleSeriesRef.current && volumeSeriesRef.current && data && data.length > 0) {
      const formattedCandles = data.map(item => ({
        time: item.timestamp,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
      }));

      const formattedVolumes = data.map(item => ({
        time: item.timestamp,
        value: item.volume,
        color: item.close >= item.open ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)',
      }));

      candleSeriesRef.current.setData(formattedCandles);
      volumeSeriesRef.current.setData(formattedVolumes);
    }
  }, [data]);

  // Update des Zeitraums
  useEffect(() => {
    if (chartRef.current && data && data.length > 0) {
      const now = Math.floor(Date.now() / 1000);
      let fromTime;
      
      switch (timeRange) {
        case '1h':
          fromTime = now - 3600;
          break;
        case '24h':
          fromTime = now - 86400;
          break;
        case '30d':
          fromTime = now - 2592000;
          break;
        default:
          fromTime = now - 86400;
      }
      
      chartRef.current.timeScale().setVisibleRange({
        from: fromTime,
        to: now,
      });
    }
  }, [timeRange, data]);

  return (
    <div className="crypto-chart rounded-lg overflow-hidden bg-gray-800 border border-gray-700">
      <div className="p-3 border-b border-gray-700">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-gray-100">{symbol}/USD</h3>
          <div className="text-right">
            <div className="font-medium text-gray-100">
              ${currentPrice?.toLocaleString(undefined, { 
                minimumFractionDigits: currentPrice < 1 ? 4 : 2, 
                maximumFractionDigits: currentPrice < 1 ? 4 : 2 
              })}
            </div>
            <div className={priceChange >= 0 ? "text-green-500 text-sm" : "text-red-500 text-sm"}>
              {priceChange >= 0 ? '↑' : '↓'} {Math.abs(priceChange || 0).toFixed(2)}%
            </div>
          </div>
        </div>
      </div>
      <div ref={chartContainerRef} className="chart-container h-[300px]" />
    </div>
  );
};

export default CryptoChart;
